import { type MP4Info } from "mp4box";

import { PreviewValidationError, PreviewValidationErrors } from "../models";

import { computeDisplayRotationDegrees } from "./computeDisplayRotationDegrees";
import { isValidAspectRatio } from "./isValidAspectRatio";
import { isValidDimensions } from "./isValidDimensions";
import { getFileMetadata } from "./videoFileMetadata";

export const DURATION_LIMIT_SECS = 10;
export const PERMITTED_VIDEO_CODEC = "avc";
export const GAME_LENS_MIN_DURATION_SECS = 5;
export const GAME_LENS_MAX_DURATION_EXCLUSIVE_SECS = 11;

export interface VideoDurationRequirement {
    minDurationSecs?: number;
    maxDurationSecs?: number;
    maxDurationSecsExclusive?: number;
    minDurationErrorMessage?: PreviewValidationErrors;
    maxDurationErrorMessage?: PreviewValidationErrors;
}

export const DEFAULT_VIDEO_DURATION_REQUIREMENT: VideoDurationRequirement = {
    maxDurationSecs: DURATION_LIMIT_SECS,
    maxDurationErrorMessage: PreviewValidationErrors.TooLongDuration,
};

export const GAME_LENS_VIDEO_DURATION_REQUIREMENT: VideoDurationRequirement = {
    minDurationSecs: GAME_LENS_MIN_DURATION_SECS,
    maxDurationSecsExclusive: GAME_LENS_MAX_DURATION_EXCLUSIVE_SECS,
    minDurationErrorMessage: PreviewValidationErrors.GameLensVideoTooShort,
    maxDurationErrorMessage: PreviewValidationErrors.GameLensVideoDurationOutOfRange,
};

const getDurationValidationError = (
    durationSecs: number,
    {
        minDurationSecs,
        maxDurationSecs,
        maxDurationSecsExclusive,
        minDurationErrorMessage,
        maxDurationErrorMessage,
    }: VideoDurationRequirement,
): PreviewValidationErrors | null => {
    if (minDurationSecs !== undefined && durationSecs < minDurationSecs) {
        return minDurationErrorMessage ?? maxDurationErrorMessage ?? null;
    }

    if (maxDurationSecsExclusive !== undefined && durationSecs >= maxDurationSecsExclusive) {
        return maxDurationErrorMessage ?? minDurationErrorMessage ?? null;
    }

    if (maxDurationSecs !== undefined && durationSecs > maxDurationSecs) {
        return maxDurationErrorMessage ?? minDurationErrorMessage ?? null;
    }

    return null;
};

export const checkMP4Info = (
    info: MP4Info,
    videoDurationRequirement: VideoDurationRequirement = DEFAULT_VIDEO_DURATION_REQUIREMENT,
): void => {
    try {
        const { duration, timescale, videoTracks } = info;
        if (!videoTracks[0]) {
            // assumes single video track
            throw new Error("Missing video track.");
        }
        const { codec, track_height: height, track_width: width, matrix } = videoTracks[0];
        const rotation = computeDisplayRotationDegrees(matrix);

        // duration is in time units
        // timescale is in units/seconds
        // duration / timescale = durationSecs is seconds
        const durationSecs = duration / timescale;
        if (!codec.toLowerCase().includes(PERMITTED_VIDEO_CODEC)) {
            throw new PreviewValidationError(PreviewValidationErrors.VideoIncorrectCodec);
        } else if (!isValidAspectRatio(width, height, rotation)) {
            throw new PreviewValidationError(PreviewValidationErrors.VideoIncorrectAspectRatio);
        } else if (isValidDimensions(width, height, rotation)) {
            const durationValidationError = getDurationValidationError(durationSecs, videoDurationRequirement);

            if (durationValidationError) {
                throw new PreviewValidationError(durationValidationError);
            }
        } else {
            throw new PreviewValidationError(PreviewValidationErrors.VideoIncorrectDimensions);
        }
    } catch (error) {
        if (error instanceof PreviewValidationError) {
            throw error;
        } else {
            // this is likely runtime error, such as video track is missing
            throw new PreviewValidationError(PreviewValidationErrors.IncorrectVideoFormat);
        }
    }
};

export const checkVideoRequirements = (
    file: File,
    videoDurationRequirement: VideoDurationRequirement = DEFAULT_VIDEO_DURATION_REQUIREMENT,
): Promise<void> =>
    getFileMetadata(file).then((info) => {
        checkMP4Info(info, videoDurationRequirement);
    });
